import React, { useState, useEffect } from "react";
import { Play, X } from "lucide-react";
import { REELS } from "../mock";
import { videoStarted, videoStopped } from "../lib/mediaBus";

export default function VideoReel() {
  const [playing, setPlaying] = useState(null); // unique key of the playing card

  useEffect(() => {
    if (playing === null) return;
    videoStarted();
    return () => videoStopped();
  }, [playing]);

  // Duplicate the list so the marquee loops seamlessly (translateX -50%).
  const track = [...REELS.videos, ...REELS.videos];

  return (
    <section className="py-20 md:py-24 bg-[#16110E] text-[#F3EAD7] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8 text-center">
        <span className="label-mono text-[#F0C766]">{REELS.eyebrow}</span>
        <h2 className="font-display font-black text-4xl md:text-6xl mt-3">
          {REELS.title}
        </h2>
        <p className="mt-4 text-[#B7AC97]">{REELS.blurb}</p>
      </div>

      <div className="mt-12 relative">
        {/* edge fades */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 md:w-28 z-10 bg-gradient-to-r from-[#16110E] to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 md:w-28 z-10 bg-gradient-to-l from-[#16110E] to-transparent" />

        <div
          className="marquee-track gap-5 md:gap-7 px-3"
          style={{
            animationDuration: "55s",
            animationPlayState: playing !== null ? "paused" : "running",
          }}
        >
          {track.map((v, i) => {
            const key = `${v.id}-${i}`;
            const isPlaying = playing === key;
            return (
              <div
                key={key}
                className="shrink-0 w-[210px] md:w-[260px] rounded-2xl overflow-hidden bg-black border border-[#F3EAD7]/12 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]"
              >
                <div className="relative aspect-[9/16]">
                  {isPlaying ? (
                    <>
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${v.id}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
                        title={v.label}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                      <button
                        onClick={() => setPlaying(null)}
                        className="absolute top-2 right-2 z-20 w-9 h-9 rounded-full bg-black/70 hover:bg-[#D5521B] text-[#F3EAD7] flex items-center justify-center transition-colors"
                        aria-label="Stop video and resume slideshow"
                        title="Close"
                      >
                        <X size={18} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setPlaying(key)}
                      className="group absolute inset-0 focus:outline-none focus:ring-2 focus:ring-[#D5521B]"
                      aria-label={`Play video: ${v.label}`}
                    >
                      <img
                        src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`}
                        alt={v.label}
                        className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity"
                      />
                      <span className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-14 h-14 rounded-full bg-[#D5521B] flex items-center justify-center shadow-[0_10px_28px_-6px_rgba(213,82,27,0.7)] transition-transform duration-300 group-hover:scale-110">
                          <Play size={24} className="text-[#FBF6EA] ml-0.5" fill="currentColor" />
                        </span>
                      </span>
                      <span className="absolute bottom-3 left-3 right-3 flex items-start justify-center text-center label-mono text-[#F3EAD7] whitespace-pre-line min-h-[2.6em]">
                        {v.label}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
